'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Done2GoLogo, Ico, CheckCircle, useReveal, useRevealStagger, useRevealLeft, useRevealRight, useRevealScale, useCounter, FAQItem, INDUSTRIES, FAQS } from './d2g-shared';
import LanguageSwitcher from './LanguageSwitcher';
import { Language, DEFAULT_LANGUAGE, detectBrowserLanguage, getTranslations } from '@/lib/i18n';

// ── WhatsApp live chat hook ──────────────────────────────────────────────────
interface WaMessage {
  text: string;
  out: boolean;
  time: string;
  typingDelay: number; // ms to show typing indicator before this message appears
  readDelay: number;   // ms to wait after message appears before next
}

function useWaChat(messages: WaMessage[]) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let cancelled = false;

    const reveal = async () => {
      // Initial pause before conversation starts
      await new Promise(r => setTimeout(r, 800));
      const msgs = messagesRef.current;
      for (let i = 0; i < msgs.length; i++) {
        if (cancelled) return;
        const msg = msgs[i];
        // Only show typing indicator for incoming (AI) messages
        if (!msg.out) {
          setShowTyping(true);
          await new Promise(r => setTimeout(r, msg.typingDelay));
          if (cancelled) return;
          setShowTyping(false);
          await new Promise(r => setTimeout(r, 80));
        }
        if (cancelled) return;
        setVisibleCount(i + 1);
        await new Promise(r => setTimeout(r, msg.readDelay));
      }
      // Pause at end, then restart
      if (!cancelled) {
        await new Promise(r => setTimeout(r, 4000));
        if (!cancelled) {
          setVisibleCount(0);
          setShowTyping(false);
          await new Promise(r => setTimeout(r, 600));
          reveal();
        }
      }
    };

    reveal();
    return () => { cancelled = true; };
  }, [started]);

  return { visibleCount, showTyping, containerRef };
}

export default function Done2GoPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', branche: '', website: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const [heroParallax, setHeroParallax] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem('d2g-lang') as Language | null;
    if (saved) {
      setLanguage(saved);
    } else {
      setLanguage(detectBrowserLanguage());
    }
  }, []);

  const handleLanguageChange = useCallback((lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('d2g-lang', lang);
  }, []);

  const t = getTranslations(language);

  const c1 = useCounter(15);
  const c2 = useCounter(0);
  const c3 = useCounter(100);
  const c4 = useCounter(48);

  const r1 = useReveal(); const r2 = useReveal(); const r3 = useReveal();
  const r4 = useReveal(); const r5 = useReveal(); const r6 = useReveal();
  const r7 = useReveal(); const r8 = useReveal(); const r9 = useReveal();
  const r10 = useReveal();

  // New stagger/directional reveal refs
  const pricingStagger = useRevealStagger();
  const industriesStagger = useRevealStagger();
  const heroLeft = useRevealLeft();
  const heroRight = useRevealRight();
  const pricingHeading = useRevealScale();

  // WhatsApp chat messages — realistic full conversation
  const waMessages: WaMessage[] = useMemo(() => [
    { text: t.ai.chat1, out: false, time: '14:02', typingDelay: 1200, readDelay: 1400 },
    { text: t.ai.chat2, out: true,  time: '14:02', typingDelay: 0,    readDelay: 900  },
    { text: t.ai.chat3, out: false, time: '14:03', typingDelay: 1500, readDelay: 1200 },
    { text: t.ai.chat4, out: true,  time: '14:03', typingDelay: 0,    readDelay: 800  },
    { text: t.ai.chat5, out: false, time: '14:03', typingDelay: 1800, readDelay: 1000 },
    { text: t.ai.chat6, out: true,  time: '14:04', typingDelay: 0,    readDelay: 900  },
    { text: t.ai.chat7, out: false, time: '14:04', typingDelay: 1400, readDelay: 1600 },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [language]);
  const { visibleCount, showTyping, containerRef: waChatRef } = useWaChat(waMessages);

  useEffect(() => {
    const onScroll = () => {
      const sy = window.scrollY;
      setScrolled(sy > 20);
      const d = document.documentElement;
      const h = d.scrollHeight - d.clientHeight;
      setProgress(h > 0 ? (sy / h) * 100 : 0);
      setHeroParallax(sy * 0.28);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nav = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const navLinks = [
    { href: '#leistungen', label: t.nav.leistungen },
    { href: '#modell', label: t.nav.modell },
    { href: '#preise', label: t.nav.preise },
    { href: '#referenzen', label: t.nav.referenzen },
    { href: '#faq', label: t.nav.faq },
    { href: '#kontakt', label: t.nav.kontakt },
  ];

  const cardHover = {
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(16,38,52,0.12)'; },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; },
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const to = 'dasilva@done2go.com';
    const subject = encodeURIComponent(`Contacto Done2Go - ${formData.name}`);
    const body = encodeURIComponent(
      `Nome: ${formData.name}\nEmail: ${formData.email}\nSetor: ${formData.branche}\nWebsite: ${formData.website}\n\nMensagem:\n${formData.message}`
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const arrowSvg = (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // Stat animation delays
  const statDelays = ['0.05s', '0.18s', '0.31s', '0.44s'];

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', -apple-system, system-ui, sans-serif", background: '#F8FAFC', color: '#1F2933' }}>

      {/* Progress Bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, height: 3, background: 'linear-gradient(90deg,#F26522,#ff9a5c)', width: `${progress}%`, zIndex: 9999, transition: 'width 0.1s linear', borderRadius: '0 2px 2px 0' }} />

      {/* NAV */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(248,250,252,0.88)', backdropFilter: 'saturate(1.6) blur(14px)', WebkitBackdropFilter: 'saturate(1.6) blur(14px)', borderBottom: scrolled ? '1px solid #e4eaf0' : '1px solid transparent', boxShadow: scrolled ? '0 1px 0 rgba(16,38,52,0.03)' : 'none', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
        <div className="wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68, minHeight: 68 }}>
          <a href="#top" onClick={(e) => nav(e, '#top')} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', height: '100%' }}><Done2GoLogo height={34} /></a>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="d2g-desktop-nav">
            {navLinks.map(l => (
              <a key={l.href} href={l.href} onClick={(e) => nav(e, l.href)} className="nav-link">{l.label}</a>
            ))}
          </nav>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }} className="d2g-desktop-cta">
            <LanguageSwitcher currentLanguage={language} onLanguageChange={handleLanguageChange} />
            <a href="#modell" onClick={(e) => nav(e, '#modell')} className="btn btn-ghost" style={{ padding: '10px 18px', fontSize: 14 }}>{t.nav.howItWorks}</a>
            <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-primary" style={{ padding: '10px 20px', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.nav.requestDemo} {arrowSvg}</a>
          </div>
          <button className="d2g-mobile-btn" onClick={() => setMobileOpen(!mobileOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#214764', padding: 8, display: 'none', alignItems: 'center', justifyContent: 'center' }}>
            {mobileOpen
              ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              : <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            }
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#102634', zIndex: 200, display: 'flex', flexDirection: 'column', padding: '80px 28px 40px', gap: 8 }}>
          <button onClick={() => setMobileOpen(false)} style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
          <div style={{ position: 'absolute', top: 20, left: 20 }}>
            <LanguageSwitcher currentLanguage={language} onLanguageChange={handleLanguageChange} dark />
          </div>
          {navLinks.map(l => (
            <a key={l.href} href={l.href} onClick={(e) => nav(e, l.href)} style={{ color: 'rgba(255,255,255,0.85)', fontSize: 22, fontWeight: 600, padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', textDecoration: 'none', fontFamily: "'Schibsted Grotesk', sans-serif" }}>{l.label}</a>
          ))}
          <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-primary" style={{ marginTop: 24, justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.nav.requestDemo} {arrowSvg}</a>
        </div>
      )}

      {/* HERO */}
      <section id="top" style={{ background: 'radial-gradient(1100px 520px at 78% -8%,rgba(242,101,34,.16),transparent 60%),radial-gradient(900px 600px at 8% 110%,rgba(86,140,180,.18),transparent 55%),linear-gradient(168deg,#16344a 0%,#102634 100%)', color: '#fff', overflow: 'hidden', position: 'relative', paddingTop: 100, paddingBottom: 80 }}>
        {/* Parallax orbs */}
        <div className="hero-orb-1" style={{ transform: `translateY(${heroParallax * 0.4}px)` }} />
        <div className="hero-orb-2" style={{ transform: `translateY(${heroParallax * 0.25}px)` }} />
        <div className="hero-orb-3" style={{ transform: `translateY(${heroParallax * 0.15}px)` }} />
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            {/* Hero left — reveal from left */}
            <div ref={heroLeft as React.RefObject<HTMLDivElement>} className="reveal-left visible" style={{ opacity: 1, transform: 'none' }}>
              <div className="pill-eyebrow" style={{ marginBottom: 28, animation: 'wordIn 0.6s cubic-bezier(0.25,0.46,0.45,0.94) 0.1s both' }}>
                <span className="pill-dot" />
                {t.hero.eyebrow}
              </div>
              {/* Headline with diagonal silver shimmer */}
              <h1 className="hero-headline-shimmer" style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 'clamp(40px,5.5vw,72px)', letterSpacing: '-0.035em', lineHeight: 1.04, color: '#fff', margin: '0 0 24px' }}>
                <span className="hero-word" style={{ animationDelay: '0.2s' }}>{t.hero.headline1}</span><br />
                <span className="hero-word" style={{ color: '#F26522', animationDelay: '0.35s' }}>{t.hero.headline2}</span><br />
                <span className="hero-word" style={{ animationDelay: '0.5s' }}>{t.hero.headline3}</span>
              </h1>
              <p className="hero-cta-enter" style={{ color: 'rgba(255,255,255,0.74)', fontSize: 'clamp(17px,2vw,20px)', maxWidth: '52ch', lineHeight: 1.55, marginBottom: 36 }} dangerouslySetInnerHTML={{ __html: t.hero.subtext }} />
              <div className="hero-cta-enter" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40, animationDelay: '0.85s' }}>
                <a href="#modell" onClick={(e) => nav(e, '#modell')} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.hero.cta1} {arrowSvg}</a>
                <a href="#leistungen" onClick={(e) => nav(e, '#leistungen')} className="btn btn-light">{t.hero.cta2}</a>
              </div>
              <div className="hero-badges-enter" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', color: 'rgba(255,255,255,0.62)', fontSize: 14 }}>
                {[
                  { d: 'M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z M9 12l2 2 4-4', t: t.hero.badge1 },
                  { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', t: t.hero.badge2 },
                  { d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z', t: t.hero.badge3 },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="trust-icon" style={{ color: '#F26522' }}><Ico d={item.d} size={16} /></span>
                    <span>{item.t}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Hero right — browser mock with animated buttons */}
            <div style={{ animation: 'float 6s ease-in-out infinite', opacity: 0, transform: 'translateX(36px)', animationFillMode: 'forwards', animationName: 'float, heroRightIn', animationDuration: '6s, 0.8s', animationDelay: '0s, 0.4s', animationTimingFunction: 'ease-in-out, cubic-bezier(0.25,0.46,0.45,0.94)', animationIterationCount: 'infinite, 1' }}>
              <div className="browser-mock">
                {/* Browser chrome with animated traffic-light buttons */}
                <div style={{ background: '#f1f5f8', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #e4eaf0' }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <div className="browser-btn-red" />
                    <div className="browser-btn-yellow" />
                    <div className="browser-btn-green" />
                  </div>
                  <div style={{ flex: 1, background: '#fff', border: '1px solid #e1e8ee', borderRadius: 7, padding: '5px 12px', fontSize: 12, color: '#5b6b78', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><circle cx="5" cy="5" r="4" stroke="#28c840" strokeWidth="1.2" /><path d="M3 5l1.5 1.5L7 3.5" stroke="#28c840" strokeWidth="1.2" strokeLinecap="round" /></svg>
                    {t.hero.browserUrl}
                  </div>
                </div>
                <div style={{ padding: 24, background: '#fff' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F26522', marginBottom: 8 }}>{t.hero.browserEyebrow}</div>
                  <h4 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#214764', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: 10 }}>{t.hero.browserTitle}</h4>
                  <p style={{ fontSize: 13, color: '#5b6b78', lineHeight: 1.55, marginBottom: 16, maxWidth: '34ch' }}>{t.hero.browserDesc}</p>
                  <div style={{ display: 'inline-flex', background: '#F26522', color: '#fff', fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 8, marginBottom: 16 }}>{t.hero.browserCta}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[t.hero.browserTag1, t.hero.browserTag2, t.hero.browserTag3].map(tag => <span key={tag} style={{ fontSize: 10, color: '#5b6b78', background: '#f1f5f8', border: '1px solid #e4eaf0', padding: '4px 9px', borderRadius: 999 }}>{tag}</span>)}
                  </div>
                </div>
                <div style={{ background: '#16344a', height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: 'ui-monospace,monospace', letterSpacing: '0.02em' }}>{t.hero.imagePlaceholder}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 80, overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 40 }}>
          <p style={{ textAlign: 'center', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontWeight: 600, marginBottom: 20 }}>{t.hero.marqueeLabel}</p>
          <div style={{ overflow: 'hidden' }}>
            <div className="marquee-track">
              {[...t.hero.marqueeItems, ...t.hero.marqueeItems].map((item, i) => (
                <span key={i} style={{ whiteSpace: 'nowrap', padding: '8px 20px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.15)', fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section style={{ background: '#fff', borderBottom: '1px solid #e4eaf0', padding: '18px 0' }}>
        <div className="wrap">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
            {[
              { d: 'M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z M9 12l2 2 4-4', label: t.trust.item1 },
              { d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z', label: t.trust.item2 },
              { d: 'M18 6.5A7 7 0 1 0 18 17.5M3 10h10M3 14h10', label: t.trust.item3 },
              { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', label: t.trust.item4 },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: '#5b6b78' }}>
                <span className="trust-icon" style={{ color: '#F26522' }}><Ico d={item.d} size={18} /></span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section style={{ background: '#16344a', padding: '72px 0' }}>
        <div className="wrap">
          <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32 }}>
            {[
              { data: c1, unit: t.stats.unit1, desc: t.stats.desc1 },
              { data: c2, unit: t.stats.unit2, desc: t.stats.desc2 },
              { data: c3, unit: t.stats.unit3, desc: t.stats.desc3 },
              { data: c4, unit: t.stats.unit4, desc: t.stats.desc4 },
            ].map((s, i) => (
              <div key={i} ref={s.data.ref} className="stat-item" style={{ textAlign: 'center', padding: '16px 8px', borderRadius: 16, cursor: 'default' }}>
                <div className="stat-value-shimmer" style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 'clamp(36px,4vw,52px)', letterSpacing: '-0.04em', color: '#F26522', lineHeight: 1, display: 'inline-block' }}>
                  <span style={{ animation: `stat-pop 0.55s var(--spring) ${statDelays[i]} both` }}>{s.data.count}{s.unit}</span>
                </div>
                <div className="stat-accent-line" style={{ animationDelay: statDelays[i] }} />
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginTop: 10, lineHeight: 1.4 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="leistungen" style={{ padding: '100px 0' }}>
        <div className="wrap">
          <div ref={r1} className="reveal" style={{ maxWidth: 680, marginBottom: 64 }}>
            <span className="eyebrow">{t.problem.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.problem.headline}</h2>
            <p className="lead">{t.problem.lead}</p>
          </div>
          <div className="problem-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            {[
              { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', title: t.problem.card1Title, desc: t.problem.card1Desc, tag: t.problem.card1Tag },
              { d: 'M18 6.5A7 7 0 1 0 18 17.5M3 10h10M3 14h10', title: t.problem.card2Title, desc: t.problem.card2Desc, tag: t.problem.card2Tag },
              { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M8 12h8M12 8v8', title: t.problem.card3Title, desc: t.problem.card3Desc, tag: t.problem.card3Tag },
            ].map((item, i) => (
              <div key={i} className="card" style={{ padding: 32 }} {...cardHover}>
                <div className="icon-box" style={{ width: 52, height: 52, borderRadius: 14, background: '#fde9df', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F26522', marginBottom: 20 }}><Ico d={item.d} size={28} /></div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#214764', marginBottom: 12, letterSpacing: '-0.02em' }}>{item.title}</h3>
                <p style={{ fontSize: 15, color: '#5b6b78', lineHeight: 1.6, marginBottom: 20 }}>{item.desc}</p>
                <div style={{ display: 'inline-flex', background: '#fde9df', color: '#F26522', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8 }}>{item.tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODEL */}
      <section id="modell" style={{ background: '#16344a', padding: '100px 0' }}>
        <div className="wrap">
          <div ref={r2} className="reveal" style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 72px' }}>
            <span className="eyebrow" style={{ color: '#F26522' }}>{t.model.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#fff' }}>{t.model.headline}</h2>
            <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{t.model.subtext}</p>
          </div>
          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            {[
              { stepLabel: t.model.step1Label, title: t.model.step1Title, desc: t.model.step1Desc, d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M8 12h8M12 8v8' },
              { stepLabel: t.model.step2Label, title: t.model.step2Title, desc: t.model.step2Desc, d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z' },
              { stepLabel: t.model.step3Label, title: t.model.step3Title, desc: t.model.step3Desc, d: 'M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z M9 12l2 2 4-4' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: '36px 32px', position: 'relative', transition: 'transform 0.35s, box-shadow 0.35s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#F26522' }}>{item.stepLabel}</span>
                  <div className="step-icon-box" style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(242,101,34,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F26522' }}><Ico d={item.d} size={22} /></div>
                </div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 14, letterSpacing: '-0.02em' }}>{item.title}</h3>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', lineHeight: 1.65 }}>{item.desc}</p>
                <div style={{ position: 'absolute', bottom: 24, right: 24, width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 22, color: 'rgba(255,255,255,0.15)' }}>{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="preise" style={{ padding: '100px 0' }}>
        <div className="wrap">
          <div ref={pricingHeading as React.RefObject<HTMLDivElement>} className="reveal-scale" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 64px' }}>
            <span className="eyebrow">{t.pricing.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.pricing.headline}</h2>
            <p className="lead">{t.pricing.lead}</p>
          </div>
          {/* Pricing cards with staggered reveal */}
          <div ref={pricingStagger as React.RefObject<HTMLDivElement>} className="reveal-stagger pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, marginBottom: 48 }}>
            {[
              { tier: t.pricing.tier1Label, name: t.pricing.tier1Name, price: t.pricing.tier1Price, unit: t.pricing.tier1Unit, desc: t.pricing.tier1Desc, features: [t.pricing.tier1F1, t.pricing.tier1F2, t.pricing.tier1F3, t.pricing.tier1F4, t.pricing.tier1F5], featured: false },
              { tier: t.pricing.tier2Label, name: t.pricing.tier2Name, price: t.pricing.tier2Price, unit: t.pricing.tier2Unit, desc: t.pricing.tier2Desc, features: [t.pricing.tier2F1, t.pricing.tier2F2, t.pricing.tier2F3, t.pricing.tier2F4], featured: true, badge: t.pricing.tier2Badge },
              { tier: t.pricing.tier3Label, name: t.pricing.tier3Name, price: t.pricing.tier3Price, unit: t.pricing.tier3Unit, desc: t.pricing.tier3Desc, features: [t.pricing.tier3F1, t.pricing.tier3F2, t.pricing.tier3F3, t.pricing.tier3F4, t.pricing.tier3F5], featured: false },
            ].map((pkg, i) => (
              <div key={i}
                className={`pricing-card${pkg.featured ? ' featured-card' : ''}`}
                style={{ borderRadius: 18, padding: pkg.featured ? '52px 32px 36px' : '36px 32px', position: 'relative', ...(pkg.featured ? { background: 'linear-gradient(145deg,#16344a,#102634)', color: '#fff' } : { background: '#fff', border: '1px solid #e4eaf0' }) }}
              >
                {pkg.featured && <div className="featured-card-glow" />}
                {pkg.badge && (
                  <div className="badge-wrapper">
                    <span className="badge-glow" style={{ display: 'inline-block', background: 'linear-gradient(90deg,#F26522,#ff7a3d)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 16px', borderRadius: 999, letterSpacing: '0.1em', textTransform: 'uppercase', boxShadow: '0 4px 16px rgba(242,101,34,0.45)', whiteSpace: 'nowrap' }}>{pkg.badge}</span>
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#F26522', marginBottom: 12 }}>{pkg.tier}</div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: pkg.featured ? '#fff' : '#214764', marginBottom: 12, letterSpacing: '-0.02em' }}>{pkg.name}</h3>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: pkg.price === t.pricing.tier3Price && !pkg.unit ? 24 : 40, letterSpacing: '-0.04em', color: pkg.featured ? '#fff' : '#214764' }}>{pkg.price}</span>
                  {pkg.unit && <span style={{ fontSize: 14, color: pkg.featured ? 'rgba(255,255,255,0.55)' : '#8593a0', marginLeft: 6 }}>{pkg.unit}</span>}
                </div>
                <p style={{ fontSize: 14, color: pkg.featured ? 'rgba(255,255,255,0.65)' : '#5b6b78', lineHeight: 1.6, marginBottom: 24 }}>{pkg.desc}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pkg.features.map((f, fi) => (
                    <li key={fi} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: pkg.featured ? 'rgba(255,255,255,0.8)' : '#5b6b78' }}>
                      <CheckCircle size={18} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className={pkg.featured ? 'btn btn-primary' : 'btn btn-ghost'} style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.pricing.cta} {arrowSvg}</a>
              </div>
            ))}
          </div>

          {/* HOSTING */}
          <div ref={r4} className="reveal">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <span className="eyebrow">{t.pricing.hostingEyebrow}</span>
              <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(24px,3vw,36px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 12, marginBottom: 12, color: '#214764' }}>{t.pricing.hostingHeadline}</h2>
              <p style={{ fontSize: 17, color: '#5b6b78', maxWidth: '52ch', margin: '0 auto' }}>{t.pricing.hostingSubtext}</p>
            </div>
            <div className="hosting-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}>
              {[
                { name: t.pricing.hosting1Name, price: t.pricing.hosting1Price, desc: t.pricing.hosting1Desc, feature: t.pricing.hosting1Feature, featured: false },
                { name: t.pricing.hosting2Name, price: t.pricing.hosting2Price, desc: t.pricing.hosting2Desc, feature: t.pricing.hosting2Feature, featured: true },
              ].map((plan, i) => (
                <div key={i}
                  className={`hosting-card${plan.featured ? ' hosting-featured' : ''}`}
                  style={{ borderRadius: 18, padding: 28, background: plan.featured ? '#fde9df' : '#fff', border: plan.featured ? '2px solid #F26522' : '1px solid #e4eaf0', position: 'relative' }}
                >
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: '#214764', marginBottom: 8 }}>{plan.name}</h3>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 32, letterSpacing: '-0.04em', color: '#F26522' }}>{plan.price}</span>
                    <span style={{ fontSize: 13, color: '#8593a0', marginLeft: 4 }}>{t.pricing.perMonth}</span>
                  </div>
                  <p style={{ fontSize: 13.5, color: '#5b6b78', lineHeight: 1.6, marginBottom: 16 }}>{plan.desc}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#F26522' }}>
                    <CheckCircle size={16} />
                    <span>{plan.feature}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* KI-ASSISTENT PRICING */}
          <div style={{ marginTop: 72 }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <span className="eyebrow">{t.kiPricing.eyebrow}</span>
              <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(24px,3vw,40px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 12, marginBottom: 16, color: '#214764' }}>{t.kiPricing.headline}</h2>
              <p style={{ fontSize: 17, color: '#5b6b78', maxWidth: '58ch', margin: '0 auto', lineHeight: 1.6 }}>{t.kiPricing.subtext}</p>
            </div>

            {/* Basic + Premium cards */}
            <div className="ki-cards-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 40 }}>
              {/* Basic */}
              <div style={{ borderRadius: 18, padding: '36px 32px', background: '#fff', border: '1px solid #e4eaf0', position: 'relative', transition: 'transform 0.35s, box-shadow 0.35s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(16,38,52,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#F26522', marginBottom: 12 }}>{t.kiPricing.basicLabel}</div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: '#214764', marginBottom: 8, letterSpacing: '-0.02em' }}>{t.kiPricing.basicName}</h3>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 40, letterSpacing: '-0.04em', color: '#214764' }}>{t.kiPricing.basicPrice}</span>
                  <span style={{ fontSize: 14, color: '#8593a0', marginLeft: 6 }}>{t.kiPricing.basicPerMonth}</span>
                </div>
                <div style={{ fontSize: 13, color: '#8593a0', marginBottom: 20 }}>{t.kiPricing.basicSetup}</div>
                <p style={{ fontSize: 14, color: '#5b6b78', lineHeight: 1.6, marginBottom: 24 }}>{t.kiPricing.basicDesc}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[t.kiPricing.basicF1, t.kiPricing.basicF2, t.kiPricing.basicF3, t.kiPricing.basicF4, t.kiPricing.basicF5].map((f, fi) => (
                    <li key={fi} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#5b6b78' }}>
                      <CheckCircle size={18} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.kiPricing.basicCta} {arrowSvg}</a>
              </div>

              {/* Premium */}
              <div style={{ borderRadius: 18, padding: '52px 32px 36px', background: 'linear-gradient(145deg,#16344a,#102634)', color: '#fff', position: 'relative', transition: 'transform 0.35s, box-shadow 0.35s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="featured-card-glow" />
                {/* EMPFOHLEN badge */}
                <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(90deg,#F26522,#ff7a3d)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '6px 20px', borderRadius: '0 0 12px 12px', letterSpacing: '0.1em', textTransform: 'uppercase', boxShadow: '0 4px 16px rgba(242,101,34,0.45)', whiteSpace: 'nowrap' }}>{t.kiPricing.premiumBadge}</div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#F26522', marginBottom: 12 }}>{t.kiPricing.premiumLabel}</div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 8, letterSpacing: '-0.02em' }}>{t.kiPricing.premiumName}</h3>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 40, letterSpacing: '-0.04em', color: '#fff' }}>{t.kiPricing.premiumPrice}</span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginLeft: 6 }}>{t.kiPricing.premiumPerMonth}</span>
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 20 }}>{t.kiPricing.premiumSetup}</div>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: 24 }}>{t.kiPricing.premiumDesc}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[t.kiPricing.premiumF1, t.kiPricing.premiumF2, t.kiPricing.premiumF3, t.kiPricing.premiumF4, t.kiPricing.premiumF5].map((f, fi) => (
                    <li key={fi} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
                      <CheckCircle size={18} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.kiPricing.premiumCta} {arrowSvg}</a>
              </div>
            </div>

            {/* WhatsApp Boosts subsection */}
            <div style={{ background: '#fff', border: '1px solid #e4eaf0', borderRadius: 18, padding: '36px 40px' }}>
              <div style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#214764', letterSpacing: '-0.02em' }}>{t.kiPricing.waTitle}</h3>
                </div>
                <p style={{ fontSize: 14, color: '#5b6b78', lineHeight: 1.6 }}>{t.kiPricing.waDesc}</p>
              </div>
              <div className="wa-boost-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                {[
                  { volume: t.kiPricing.wa1Volume, price: t.kiPricing.wa1Price, unit: t.kiPricing.wa1Unit },
                  { volume: t.kiPricing.wa2Volume, price: t.kiPricing.wa2Price, unit: t.kiPricing.wa2Unit },
                  { volume: t.kiPricing.wa3Volume, price: t.kiPricing.wa3Price, unit: t.kiPricing.wa3Unit },
                  { volume: t.kiPricing.wa4Volume, price: t.kiPricing.wa4Price, unit: t.kiPricing.wa4Unit },
                ].map((boost, i) => (
                  <div key={i} style={{ background: '#F8FAFC', border: '1px solid #e4eaf0', borderRadius: 14, padding: '20px 16px', textAlign: 'center', transition: 'border-color 0.2s, background 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#25D366'; e.currentTarget.style.background = '#f0fdf4'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e4eaf0'; e.currentTarget.style.background = '#F8FAFC'; }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 8 }}>{boost.volume}</div>
                    <div style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 22, color: '#214764', letterSpacing: '-0.03em', lineHeight: 1 }}>{boost.price}</div>
                    {boost.unit && <div style={{ fontSize: 12, color: '#8593a0', marginTop: 4 }}>{boost.unit}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI ASSISTANT */}
      <section id="ki" style={{ background: '#16344a', padding: '100px 0' }}>
        <div className="wrap">
          <div className="ai-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
            <div ref={r5} className="reveal">
              <span className="eyebrow" style={{ color: '#F26522' }}>{t.ai.eyebrow}</span>
              <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,42px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#fff' }}>{t.ai.headline}</h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 32 }}>{t.ai.subtext}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 36px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[t.ai.f1, t.ai.f2, t.ai.f3, t.ai.f4, t.ai.f5].map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: 'rgba(255,255,255,0.8)' }}>
                    <CheckCircle size={18} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {/* ROI Block */}
              <div style={{ background: 'rgba(242,101,34,0.1)', border: '1px solid rgba(242,101,34,0.3)', borderRadius: 16, padding: '24px 28px', marginBottom: 32 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F26522', marginBottom: 14 }}>{t.ai.roiLabel}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {[
                    { value: t.ai.roi1Value, desc: t.ai.roi1Desc },
                    { value: t.ai.roi2Value, desc: t.ai.roi2Desc },
                    { value: t.ai.roi3Value, desc: t.ai.roi3Desc },
                    { value: t.ai.roi4Value, desc: t.ai.roi4Desc },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: '-0.03em', color: '#F26522', lineHeight: 1 }}>{item.value}</span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.ai.cta} {arrowSvg}</a>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 20px', borderRadius: 11, border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 500 }}>
                  <span style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 20, color: '#F26522' }}>99 €</span> {t.ai.perMonth}
                </div>
              </div>
            </div>

            {/* Telegram Live Chat — animated real-time conversation */}
            <div style={{ animation: 'float 7s ease-in-out infinite' }}>
              <div style={{ background: '#17212b', borderRadius: 20, overflow: 'hidden', boxShadow: '0 30px 70px rgba(0,0,0,0.4)' }}>
                {/* Telegram header */}
                <div style={{ background: '#17212b', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#2AABEE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 700, fontSize: 14, color: '#fff' }}>D2</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>{t.ai.chatName}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="tg-online-dot" /> {t.ai.chatOnline}
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.5)' }}>
                    {/* Telegram paper plane icon */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
                  </div>
                </div>

                {/* Chat messages area */}
                <div ref={waChatRef} style={{ background: '#0e1621', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 300, overflowY: 'hidden' }}>
                  {waMessages.map((msg, i) => (
                    <div key={i} className={`wa-message${i < visibleCount ? ' visible' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.out ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        borderRadius: msg.out ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
                        background: msg.out ? '#2b5278' : '#182533',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        padding: '8px 12px',
                        fontSize: 13.5,
                        lineHeight: 1.5,
                        color: '#e8f1f8',
                        maxWidth: 240
                      }}>{msg.text}</div>
                      <div className={`wa-time${msg.out ? ' outgoing' : ''}`} style={{ color: 'rgba(255,255,255,0.35)' }}>
                        {msg.time}
                        {msg.out ? (
                          <svg width="14" height="10" viewBox="0 0 16 11" fill="none" style={{ marginLeft: 3 }}>
                            <path d="M1 5.5l4 4L15 1" stroke="#2AABEE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5 5.5l4 4" stroke="#2AABEE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : null}
                      </div>
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {showTyping && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div className="tg-typing">
                        <div className="tg-typing-dot" />
                        <div className="tg-typing-dot" />
                        <div className="tg-typing-dot" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Input bar */}
                <div style={{ background: '#17212b', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ flex: 1, background: '#242f3d', borderRadius: 24, padding: '8px 14px', fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>Nachricht schreiben…</div>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#2AABEE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DATA SCIENCE / KPI */}
      <section id="datascience" style={{ background: '#F8FAFC', padding: '100px 0', borderTop: '1px solid #e4eaf0' }}>
        <div className="wrap">
          <div ref={r10} className="reveal" style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 64px' }}>
            <span className="eyebrow">{t.ds.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.ds.headline}</h2>
            <p className="lead">{t.ds.lead}</p>
          </div>
          <div className="ds-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, marginBottom: 56 }}>
            {[
              { d: 'M3 3h18v18H3zM3 9h18M9 21V9', title: t.ds.card1Title, desc: t.ds.card1Desc, tag: t.ds.card1Tag, color: '#214764' },
              { d: 'M18 20V10M12 20V4M6 20v-6', title: t.ds.card2Title, desc: t.ds.card2Desc, tag: t.ds.card2Tag, color: '#F26522' },
              { d: 'M12 2a10 10 0 100 20A10 10 0 0012 2zM2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20', title: t.ds.card3Title, desc: t.ds.card3Desc, tag: t.ds.card3Tag, color: '#214764' },
            ].map((item, i) => (
              <div key={i} className="card" style={{ padding: 32 }} {...cardHover}>
                <div className="icon-box" style={{ width: 52, height: 52, borderRadius: 14, background: i === 1 ? '#fde9df' : '#e8f0f7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color, marginBottom: 20 }}><Ico d={item.d} size={26} /></div>
                <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: '#214764', marginBottom: 12, letterSpacing: '-0.02em' }}>{item.title}</h3>
                <p style={{ fontSize: 15, color: '#5b6b78', lineHeight: 1.6, marginBottom: 20 }}>{item.desc}</p>
                <div style={{ display: 'inline-flex', background: i === 1 ? '#fde9df' : '#e8f0f7', color: i === 1 ? '#F26522' : '#214764', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8 }}>{item.tag}</div>
              </div>
            ))}
          </div>
          {/* Deliverables strip */}
          <div style={{ background: '#16344a', borderRadius: 20, padding: '40px 48px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32 }}>
            {[
              { d: 'M9 17H7A5 5 0 017 7h2M15 7h2a5 5 0 010 10h-2M8 12h8', value: t.ds.strip1Value, label: t.ds.strip1Label },
              { d: 'M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z', value: t.ds.strip2Value, label: t.ds.strip2Label },
              { d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z', value: t.ds.strip3Value, label: t.ds.strip3Label },
              { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', value: t.ds.strip4Value, label: t.ds.strip4Label },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div className="strip-icon-box" style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(242,101,34,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F26522', margin: '0 auto 12px' }}><Ico d={item.d} size={22} /></div>
                <div style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontWeight: 800, fontSize: 22, color: '#F26522', letterSpacing: '-0.02em', marginBottom: 4 }}>{item.value}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.ds.cta} {arrowSvg}</a>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="referenzen" style={{ padding: '100px 0' }}>
        <div className="wrap">
          <div ref={r6} className="reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 64px' }}>
            <span className="eyebrow">{t.industries.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.industries.headline}</h2>
            <p className="lead">{t.industries.lead}</p>
          </div>
          {/* Staggered industry cards */}
          <div ref={industriesStagger as React.RefObject<HTMLDivElement>} className="reveal-stagger industry-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
            {(t.industries.items ?? INDUSTRIES).map((ind, i) => (
              <div key={i} className="industry-card">
                <img src={INDUSTRIES[i]?.img} alt={INDUSTRIES[i]?.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <div className="industry-overlay" />
                <div className="industry-content">
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#F26522', marginBottom: 6 }}>{ind.label}</div>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6, letterSpacing: '-0.02em' }}>{ind.title}</h3>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RISK FREE */}
      <section style={{ background: '#fff', padding: '100px 0', borderTop: '1px solid #e4eaf0' }}>
        <div className="wrap">
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 64px' }}>
            <span className="eyebrow">{t.riskFree.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.riskFree.headline}</h2>
            <p className="lead">{t.riskFree.lead}</p>
          </div>
          <div className="risk-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 32 }}>
            {[
              { d: 'M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z M9 12l2 2 4-4', title: t.riskFree.card1Title, desc: t.riskFree.card1Desc },
              { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', title: t.riskFree.card2Title, desc: t.riskFree.card2Desc },
              { d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z', title: t.riskFree.card3Title, desc: t.riskFree.card3Desc },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 20 }}>
                <div className="risk-icon-box" style={{ width: 60, height: 60, borderRadius: 16, background: '#fde9df', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F26522', flexShrink: 0, cursor: 'default' }}><Ico d={item.d} size={28} /></div>
                <div>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: '#214764', marginBottom: 10, letterSpacing: '-0.02em' }}>{item.title}</h3>
                  <p style={{ fontSize: 15, color: '#5b6b78', lineHeight: 1.65 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ background: '#F8FAFC', padding: '100px 0', borderTop: '1px solid #e4eaf0' }}>
        <div className="wrap">
          <div ref={r8} className="reveal" style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 56px' }}>
            <span className="eyebrow">{t.faq.eyebrow}</span>
            <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#214764' }}>{t.faq.headline}</h2>
          </div>
          <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(t.faq.items ?? FAQS).map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <a href="#kontakt" onClick={(e) => nav(e, '#kontakt')} style={{ color: '#F26522', fontWeight: 600, fontSize: 15, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>{t.faq.moreCta} {arrowSvg}</a>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="kontakt" style={{ background: '#16344a', padding: '100px 0' }}>
        <div className="wrap">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
            <div ref={r9} className="reveal">
              <span className="eyebrow" style={{ color: '#F26522' }}>{t.contact.eyebrow}</span>
              <h2 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 'clamp(28px,4vw,40px)', fontWeight: 800, letterSpacing: '-0.03em', marginTop: 16, marginBottom: 16, color: '#fff' }}>{t.contact.headline}</h2>
              <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: 36 }}>{t.contact.subtext}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 40 }}>
                {[
                  { d: 'M12 2L4 6v6c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V6l-8-4z M9 12l2 2 4-4', label: t.contact.badge1 },
                  { d: 'M12 3a9 9 0 100 18A9 9 0 0012 3z M12 7v5l3 3', label: t.contact.badge2 },
                  { d: 'M13 2L4.5 13.5H12L11 22l8.5-11.5H12L13 2z', label: t.contact.badge3 },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 15, color: 'rgba(255,255,255,0.75)' }}>
                    <span className="trust-icon" style={{ color: '#F26522' }}><Ico d={item.d} size={20} /></span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: 24, borderRadius: 16, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginBottom: 8, fontStyle: 'italic' }}>{t.contact.quote}</p>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>{t.contact.quoteAuthor}</p>
              </div>

              {/* Telegram Contact Block */}
              <div style={{ marginTop: 28, padding: 24, borderRadius: 16, background: 'rgba(42,171,238,0.08)', border: '1px solid rgba(42,171,238,0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#2AABEE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 700, color: '#2AABEE', margin: 0 }}>{t.contact.telegramTitle}</p>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0 }}>{t.contact.telegramSub}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <a href="tel:+4915216203577" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', textDecoration: 'none', fontSize: 17, fontWeight: 700, letterSpacing: '0.01em', marginBottom: 8 }} onMouseEnter={e => (e.currentTarget.style.color = '#2AABEE')} onMouseLeave={e => (e.currentTarget.style.color = '#fff')}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                      +49 152 1620 3577
                    </a>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>{t.contact.telegramScan}</p>
                  </div>
                  {/* QR Code — MECARD format for automatic contact saving */}
                  <div style={{ background: '#fff', borderRadius: 10, padding: 8, flexShrink: 0 }}>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent('MECARD:N:Done2Go;TEL:+4915216203577;EMAIL:dasilva@done2go.com;;')}`}
                      alt="QR code to add Done2Go as a contact — scan to save +4915216203577"
                      width={90}
                      height={90}
                      style={{ display: 'block', borderRadius: 6 }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 20, padding: 40 }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fde9df', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#F26522' }}><CheckCircle size={32} /></div>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: '#214764', marginBottom: 12 }}>{t.contact.successTitle}</h3>
                  <p style={{ fontSize: 15, color: '#5b6b78', lineHeight: 1.6 }}>{t.contact.successText}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: '#214764', marginBottom: 24, letterSpacing: '-0.02em' }}>{t.contact.formTitle}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 6 }}>{t.contact.labelName}</label>
                      <input type="text" placeholder={t.contact.placeholderName} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #e4eaf0', fontSize: 15, color: '#1F2933', outline: 'none', fontFamily: 'inherit' }} onFocus={e => (e.target.style.borderColor = '#F26522')} onBlur={e => (e.target.style.borderColor = '#e4eaf0')} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 6 }}>{t.contact.labelEmail}</label>
                      <input type="email" placeholder={t.contact.placeholderEmail} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #e4eaf0', fontSize: 15, color: '#1F2933', outline: 'none', fontFamily: 'inherit' }} onFocus={e => (e.target.style.borderColor = '#F26522')} onBlur={e => (e.target.style.borderColor = '#e4eaf0')} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 6 }}>{t.contact.labelBranche}</label>
                    <select value={formData.branche} onChange={e => setFormData({ ...formData, branche: e.target.value })} style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #e4eaf0', fontSize: 15, color: formData.branche ? '#1F2933' : '#8593a0', outline: 'none', background: '#fff', fontFamily: 'inherit' }} onFocus={e => (e.target.style.borderColor = '#F26522')} onBlur={e => (e.target.style.borderColor = '#e4eaf0')}>
                      <option value="">{t.contact.placeholderBranche}</option>
                      <option>{t.contact.opt1}</option>
                      <option>{t.contact.opt2}</option>
                      <option>{t.contact.opt3}</option>
                      <option>{t.contact.opt4}</option>
                      <option>{t.contact.opt5}</option>
                      <option>{t.contact.opt6}</option>
                      <option>{t.contact.opt7}</option>
                      <option>{t.contact.opt8}</option>
                      <option>{t.contact.opt9}</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 6 }}>{t.contact.labelWebsite}</label>
                    <input type="text" placeholder={t.contact.placeholderWebsite} value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #e4eaf0', fontSize: 15, color: '#1F2933', outline: 'none', fontFamily: 'inherit' }} onFocus={e => (e.target.style.borderColor = '#F26522')} onBlur={e => (e.target.style.borderColor = '#e4eaf0')} />
                  </div>
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#5b6b78', marginBottom: 6 }}>{t.contact.labelMessage}</label>
                    <textarea placeholder={t.contact.placeholderMessage} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} rows={4} style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #e4eaf0', fontSize: 15, color: '#1F2933', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} onFocus={e => (e.target.style.borderColor = '#F26522')} onBlur={e => (e.target.style.borderColor = '#e4eaf0')} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 16, padding: '16px 24px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>{t.contact.submitBtn} {arrowSvg}</button>
                  <p style={{ fontSize: 12, color: '#8593a0', textAlign: 'center', marginTop: 12 }}>{t.contact.disclaimer}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: '#102634', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '64px 0 32px' }}>
        <div className="wrap">
          <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 48, marginBottom: 48 }}>
            <div>
              <Done2GoLogo height={32} />
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, marginTop: 16, maxWidth: '32ch' }}>{t.footer.tagline}</p>
              <div style={{ marginTop: 20, fontStyle: 'italic', fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>{t.footer.quote}</div>
              <a href="https://www.instagram.com/_done2go_/" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 20, color: 'rgba(255,255,255,0.5)', fontSize: 13, textDecoration: 'none' }} onMouseEnter={e => (e.currentTarget.style.color = '#F26522')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}>@_done2go_</a>
            </div>
            {[
              { heading: t.footer.col1, links: [['#leistungen', t.footer.websites], ['#preise', t.footer.preise], ['#ki', t.footer.kiAssistent], ['#kontakt', t.footer.demoAnfragen]] },
              { heading: t.footer.col2, links: [['#modell', t.footer.dasModell], ['#referenzen', t.footer.referenzen], ['#faq', t.footer.faq], ['#kontakt', t.footer.kontakt]] },
              { heading: t.footer.col3, links: [['/impressum', t.footer.impressum], ['/datenschutz', t.footer.datenschutz], ['/agb', t.footer.agb]] },
            ].map((col) => (
              <div key={col.heading}>
                <h4 style={{ fontFamily: "'Schibsted Grotesk', sans-serif", fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 16 }}>{col.heading}</h4>
                {col.links.map(([href, label]) => (
                  <a key={href} href={href} onClick={href.startsWith('#') ? (e) => nav(e as React.MouseEvent<HTMLAnchorElement>, href) : undefined} style={{ display: 'block', fontSize: 14, color: 'rgba(255,255,255,0.45)', marginBottom: 10, textDecoration: 'none' }} onMouseEnter={e => (e.currentTarget.style.color = '#F26522')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}>{label}</a>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>{t.footer.copyright}</p>
            <div style={{ display: 'flex', gap: 20 }}>
              {[['/impressum', t.footer.impressum], ['/datenschutz', t.footer.datenschutz], ['/agb', t.footer.agb]].map(([href, label]) => (
                <a key={href} href={href} style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }} onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}>{label}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes heroRightIn {
          from { opacity: 0; transform: translateX(36px); }
          to { opacity: 1; transform: translateX(0); }
        }

        /* ── Responsive breakpoints ── */
        @media (max-width: 1024px) {
          .wrap { padding-left: 24px !important; padding-right: 24px !important; }
          .ds-grid { grid-template-columns: repeat(2,1fr) !important; }
        }

        @media (max-width: 900px) {
          .hero-grid,.ai-grid,.contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .stats-grid { grid-template-columns: repeat(2,1fr) !important; }
          .problem-grid,.steps-grid { grid-template-columns: repeat(2,1fr) !important; }
          .pricing-grid { grid-template-columns: repeat(2,1fr) !important; }
          .hosting-grid { grid-template-columns: repeat(2,1fr) !important; }
          .ki-cards-grid { grid-template-columns: 1fr !important; }
          .wa-boost-grid { grid-template-columns: repeat(2,1fr) !important; }
          .industry-grid,.testimonials-grid { grid-template-columns: repeat(2,1fr) !important; }
          .risk-grid { grid-template-columns: repeat(2,1fr) !important; }
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
          .d2g-desktop-nav,.d2g-desktop-cta { display: none !important; }
          .d2g-mobile-btn { display: flex !important; }
          .hero-grid > div:last-child { display: none; }
        }

        @media (max-width: 600px) {
          .wrap { padding-left: 16px !important; padding-right: 16px !important; }
          .stats-grid { grid-template-columns: repeat(2,1fr) !important; gap: 16px !important; }
          .problem-grid,.steps-grid,.pricing-grid,.hosting-grid,.risk-grid,.industry-grid,.testimonials-grid,.ds-grid { grid-template-columns: 1fr !important; }
          .ki-cards-grid { grid-template-columns: 1fr !important; }
          .wa-boost-grid { grid-template-columns: repeat(2,1fr) !important; }
          .footer-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .pricing-card.featured-card { transform: scale(1) !important; }
          .contact-grid { gap: 32px !important; }
          .hero-grid { gap: 32px !important; }
          section { padding-top: 64px !important; padding-bottom: 64px !important; }
        }

        @media (max-width: 480px) {
          .wa-boost-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </div>
  );
}
